export type DateFormatInput = string | Date

const LOCALE = 'ja-JP-u-ca-gregory-nu-latn'
const TIME_ZONE = 'Asia/Tokyo'

const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/
const YEAR_MONTH_PATTERN = /^\d{4}-\d{2}$/
const ISO_INSTANT_PATTERN =
  /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,9}))?)?(Z|[+-](\d{2}):(\d{2}))$/

const dateWithWeekdayFormatter = new Intl.DateTimeFormat(LOCALE, {
  timeZone: TIME_ZONE,
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
  weekday: 'short',
})

const dateTimeFormatter = new Intl.DateTimeFormat(LOCALE, {
  timeZone: TIME_ZONE,
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
  weekday: 'short',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
})

type DateParts = {
  year: number
  month: number
  day: number
  weekday: string
}

type DateTimeParts = DateParts & {
  hour: string
  minute: string
}

function invalidDateInput(): never {
  throw new RangeError('Invalid date input')
}

function isLeapYear(year: number): boolean {
  return year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0)
}

function daysInMonth(year: number, month: number): number {
  if (month === 2) return isLeapYear(year) ? 29 : 28
  if ([4, 6, 9, 11].includes(month)) return 30
  return 31
}

function assertCalendarDate(year: number, month: number, day: number): void {
  if (
    year < 1 ||
    month < 1 ||
    month > 12 ||
    day < 1 ||
    day > daysInMonth(year, month)
  ) {
    invalidDateInput()
  }
}

function parseDateInput(value: DateFormatInput): Date {
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) invalidDateInput()
    return value
  }

  if (typeof value !== 'string') invalidDateInput()

  const dateOnlyMatch = DATE_ONLY_PATTERN.exec(value)
  if (dateOnlyMatch) {
    const [, yearValue, monthValue, dayValue] = dateOnlyMatch
    const year = Number(yearValue)
    const month = Number(monthValue)
    const day = Number(dayValue)
    assertCalendarDate(year, month, day)
    return new Date(`${value}T00:00:00+09:00`)
  }

  const instantMatch = ISO_INSTANT_PATTERN.exec(value)
  if (!instantMatch) invalidDateInput()

  const [
    ,
    yearValue,
    monthValue,
    dayValue,
    hourValue,
    minuteValue,
    secondValue = '0',
    ,
    ,
    offsetHourValue,
    offsetMinuteValue,
  ] = instantMatch
  const year = Number(yearValue)
  const month = Number(monthValue)
  const day = Number(dayValue)
  const hour = Number(hourValue)
  const minute = Number(minuteValue)
  const second = Number(secondValue)
  const offsetHour = offsetHourValue === undefined ? 0 : Number(offsetHourValue)
  const offsetMinute = offsetMinuteValue === undefined ? 0 : Number(offsetMinuteValue)

  assertCalendarDate(year, month, day)
  if (
    hour > 23 ||
    minute > 59 ||
    second > 59 ||
    offsetHour > 23 ||
    offsetMinute > 59
  ) {
    invalidDateInput()
  }

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) invalidDateInput()
  return date
}

function requiredPart(
  parts: Intl.DateTimeFormatPart[],
  type: Intl.DateTimeFormatPartTypes,
): string {
  const value = parts.find((part) => part.type === type)?.value
  if (value === undefined) {
    throw new Error(`Intl.DateTimeFormat did not return ${type}`)
  }
  return value
}

function extractDateParts(date: Date): DateParts {
  const parts = dateWithWeekdayFormatter.formatToParts(date)
  return {
    year: Number(requiredPart(parts, 'year')),
    month: Number(requiredPart(parts, 'month')),
    day: Number(requiredPart(parts, 'day')),
    weekday: requiredPart(parts, 'weekday'),
  }
}

function extractDateTimeParts(date: Date): DateTimeParts {
  const parts = dateTimeFormatter.formatToParts(date)
  return {
    year: Number(requiredPart(parts, 'year')),
    month: Number(requiredPart(parts, 'month')),
    day: Number(requiredPart(parts, 'day')),
    weekday: requiredPart(parts, 'weekday'),
    hour: requiredPart(parts, 'hour'),
    minute: requiredPart(parts, 'minute'),
  }
}

function currentJstYear(): number {
  return extractDateParts(new Date()).year
}

function automaticYearPrefix(year: number, todayYear: number): string {
  return year === todayYear ? '' : `${year}年`
}

export function formatJstDateBand(
  start: DateFormatInput,
  end: DateFormatInput,
): string {
  const from = extractDateParts(parseDateInput(start))
  const to = extractDateParts(parseDateInput(end))
  const todayYear = currentJstYear()
  const fromPrefix = automaticYearPrefix(from.year, todayYear)

  if (from.year === to.year) {
    const tail =
      from.month === to.month ? `${to.day}` : `${to.month}/${to.day}`
    return `${fromPrefix}${from.month}/${from.day}〜${tail}`
  }

  const toPrefix = automaticYearPrefix(to.year, todayYear)
  return `${fromPrefix}${from.month}/${from.day}〜${toPrefix}${to.month}/${to.day}`
}

export function formatJstDateBandWithWeekday(
  start: DateFormatInput,
  end: DateFormatInput,
): string {
  const from = extractDateParts(parseDateInput(start))
  const to = extractDateParts(parseDateInput(end))
  const todayYear = currentJstYear()
  const fromPrefix = automaticYearPrefix(from.year, todayYear)
  const toPrefix =
    from.year === to.year ? '' : automaticYearPrefix(to.year, todayYear)

  return `${fromPrefix}${from.month}/${from.day}（${from.weekday}）〜${toPrefix}${to.month}/${to.day}（${to.weekday}）`
}

export function formatJstCutoff(value: DateFormatInput): string {
  const parts = extractDateTimeParts(parseDateInput(value))
  const yearPrefix = automaticYearPrefix(parts.year, currentJstYear())
  return `${yearPrefix}${parts.month}/${parts.day}（${parts.weekday}）${parts.hour}:${parts.minute}`
}

export function formatJstDateTime(value: DateFormatInput): string {
  const parts = extractDateTimeParts(parseDateInput(value))
  return `${parts.year}/${parts.month}/${parts.day}（${parts.weekday}）${parts.hour}:${parts.minute}`
}

export function formatJapaneseDate(value: DateFormatInput): string {
  const parts = extractDateParts(parseDateInput(value))
  return `${parts.year}年${parts.month}月${parts.day}日`
}

export function formatJapaneseDateBand(
  start: DateFormatInput,
  end: DateFormatInput,
): string {
  const from = extractDateParts(parseDateInput(start))
  const to = extractDateParts(parseDateInput(end))

  if (from.year !== to.year) {
    return `${from.year}年${from.month}月${from.day}日〜${to.year}年${to.month}月${to.day}日`
  }

  const tail = from.month === to.month
    ? `${to.day}日`
    : `${to.month}月${to.day}日`
  return `${from.year}年${from.month}月${from.day}日〜${tail}`
}

export function formatJapaneseYearMonth(value: DateFormatInput): string {
  const normalizedValue = typeof value === 'string' && YEAR_MONTH_PATTERN.test(value)
    ? `${value}-01`
    : value
  const parts = extractDateParts(parseDateInput(normalizedValue))
  return `${parts.year}年${parts.month}月`
}
